import { useState, useEffect } from 'react'
import { supabase } from './supabase'

const FORMAT_INFO = {
  stroke_play: { label: 'STROKE PLAY', icon: '🏌️' },
  skins: { label: 'SKINS', icon: '🎯' },
  nassau: { label: 'NASSAU', icon: '3️⃣' },
}

export default function Invites({ session, onOpenLobby }) {
  const [matchInvites, setMatchInvites] = useState([])
  const [betInvites, setBetInvites] = useState([])
  const [oneVOneInvites, setOneVOneInvites] = useState([])
  const [matchProfiles, setMatchProfiles] = useState({})
  const [betProfiles, setBetProfiles] = useState({})
  const [oneVOneProfiles, setOneVOneProfiles] = useState({})
  const [tableInvites, setTableInvites] = useState([])
  const [tableProfiles, setTableProfiles] = useState({})
  const [tableDetails, setTableDetails] = useState({})
  const [betMatches, setBetMatches] = useState({})
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState({})
  const [toast, setToast] = useState(null)

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    loadAll()
    const interval = setInterval(loadAll, 15000)
    return () => clearInterval(interval)
  }, [session])

  const loadAll = async () => {
    setLoading(true)
    await Promise.all([loadMatchInvites(), loadBetInvites(), loadOneVOneInvites(), loadTableInvites()])
    setLoading(false)
  }

  const loadMatchInvites = async () => {
    const { data: mp } = await supabase.from('match_players').select('*').eq('player_id', session.user.id).eq('status', 'invited')
    if (!mp || mp.length === 0) { setMatchInvites([]); return }
    const matchIds = mp.map(i => i.match_id)
    const { data: matchData } = await supabase.from('matches').select('*').in('id', matchIds)
    const matchMap = {}
    matchData?.forEach(m => { matchMap[m.id] = m })
    const enriched = mp.map(i => ({ ...i, match: matchMap[i.match_id] || null }))
    setMatchInvites(enriched)
    const creatorIds = [...new Set(enriched.map(i => i.match?.creator_id).filter(Boolean))]
    if (creatorIds.length > 0) {
      const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name').in('id', creatorIds)
      const pmap = {}
      prof?.forEach(p => { pmap[p.id] = p })
      setMatchProfiles(pmap)
    }
  }

  const loadBetInvites = async () => {
    const { data: allLines } = await supabase.from('betting_lines').select('*').eq('status', 'pending')
    if (!allLines || allLines.length === 0) { setBetInvites([]); return }
    const pending = allLines.filter(l =>
      l.player_ids.includes(session.user.id) &&
      !(l.accepted_by || []).includes(session.user.id) &&
      l.created_by !== session.user.id
    )
    setBetInvites(pending)
    if (pending.length === 0) return
    const matchIds = [...new Set(pending.map(l => l.match_id))]
    const { data: matchData } = await supabase.from('matches').select('id, name, course_name, tee_time').in('id', matchIds)
    const mmap = {}
    matchData?.forEach(m => { mmap[m.id] = m })
    setBetMatches(mmap)
    const proposerIds = [...new Set(pending.map(l => l.created_by))]
    const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name').in('id', proposerIds)
    const pmap = {}
    prof?.forEach(p => { pmap[p.id] = p })
    setBetProfiles(pmap)
  }

  const loadOneVOneInvites = async () => {
    const { data } = await supabase.from('one_v_one').select('*').eq('opponent_id', session.user.id).eq('status', 'pending')
    setOneVOneInvites(data || [])
    if (!data || data.length === 0) return
    const ids = [...new Set(data.map(b => b.created_by))]
    const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name, username').in('id', ids)
    const pmap = {}
    prof?.forEach(p => { pmap[p.id] = p })
    setOneVOneProfiles(pmap)
  }

  const loadTableInvites = async () => {
    const { data } = await supabase.from('crossbet_table_players')
      .select('*')
      .eq('player_id', session.user.id)
      .eq('status', 'invited')
    setTableInvites(data || [])
    if (!data || data.length === 0) return
    const tableIds = [...new Set(data.map(t => t.table_id))]
    const { data: tables } = await supabase.from('crossbet_tables').select('*').in('id', tableIds)
    const tmap = {}
    tables?.forEach(t => { tmap[t.id] = t })
    setTableDetails(tmap)
    const creatorIds = [...new Set(tables?.map(t => t.created_by).filter(Boolean))]
    if (creatorIds.length > 0) {
      const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name').in('id', creatorIds)
      const pmap = {}
      prof?.forEach(p => { pmap[p.id] = p })
      setTableProfiles(pmap)
    }
  }

  const handleAcceptTable = async (invite) => {
    const t = tableDetails[invite.table_id]
    setActing(prev => ({ ...prev, [invite.id]: 'accepting' }))
    await supabase.from('crossbet_table_players').update({
      status: 'accepted',
      lives: t?.lives_per_player || 1,
      total_paid: t?.wager_amount || 0,
      joined_at: new Date().toISOString(),
    }).eq('id', invite.id)
    setActing(prev => ({ ...prev, [invite.id]: null }))
    showToast('Joined the table!', 'success')
    setTableInvites(prev => prev.filter(i => i.id !== invite.id))
  }

  const handleDeclineTable = async (invite) => {
    setActing(prev => ({ ...prev, [invite.id]: 'declining' }))
    await supabase.from('crossbet_table_players').update({ status: 'declined' }).eq('id', invite.id)
    setActing(prev => ({ ...prev, [invite.id]: null }))
    showToast('Declined', 'info')
    setTableInvites(prev => prev.filter(i => i.id !== invite.id))
  }

  const handleAcceptMatch = async (invite) => {
    setActing(prev => ({ ...prev, [invite.id]: 'accepting' }))
    const { error } = await supabase.from('match_players').update({ status: 'accepted' }).eq('id', invite.id)
    if (error) { showToast(error.message, 'error'); setActing(prev => ({ ...prev, [invite.id]: null })); return }
    showToast('Match invite accepted!', 'success')
    setMatchInvites(prev => prev.filter(i => i.id !== invite.id))
    setActing(prev => ({ ...prev, [invite.id]: null }))
  }

  const handleDeclineMatch = async (invite) => {
    setActing(prev => ({ ...prev, [invite.id]: 'declining' }))
    const { error } = await supabase.from('match_players').delete().eq('id', invite.id)
    if (error) { showToast(error.message, 'error'); setActing(prev => ({ ...prev, [invite.id]: null })); return }
    showToast('Match invite declined', 'info')
    setMatchInvites(prev => prev.filter(i => i.id !== invite.id))
    setActing(prev => ({ ...prev, [invite.id]: null }))
  }

  const handleAcceptBet = async (line) => {
    setActing(prev => ({ ...prev, [line.id]: 'accepting' }))
    const newAccepted = [...(line.accepted_by || []), session.user.id]
    const allAccepted = line.player_ids.every(pid => newAccepted.includes(pid))
    const { error } = await supabase.from('betting_lines').update({ accepted_by: newAccepted, status: allAccepted ? 'active' : 'pending' }).eq('id', line.id)
    setActing(prev => ({ ...prev, [line.id]: null }))
    if (error) return showToast(error.message, 'error')
    showToast(allAccepted ? 'Bet is now active!' : 'Accepted — waiting for others', 'success')
    setBetInvites(prev => prev.filter(l => l.id !== line.id))
  }

  const handleDeclineBet = async (line) => {
    setActing(prev => ({ ...prev, [line.id]: 'declining' }))
    const { error } = await supabase.from('betting_lines').update({ status: 'declined' }).eq('id', line.id)
    setActing(prev => ({ ...prev, [line.id]: null }))
    if (error) return showToast(error.message, 'error')
    showToast('Bet declined', 'info')
    setBetInvites(prev => prev.filter(l => l.id !== line.id))
  }

  const handleAcceptOneVOne = async (bet) => {
    setActing(prev => ({ ...prev, [bet.id]: 'accepting' }))
    await supabase.from('one_v_one').update({ status: 'active', activated_at: new Date().toISOString() }).eq('id', bet.id)
    setActing(prev => ({ ...prev, [bet.id]: null }))
    showToast('Challenge accepted!', 'success')
    setOneVOneInvites(prev => prev.filter(b => b.id !== bet.id))
  }

  const handleDeclineOneVOne = async (bet) => {
    setActing(prev => ({ ...prev, [bet.id]: 'declining' }))
    await supabase.from('one_v_one').update({ status: 'cancelled' }).eq('id', bet.id)
    setActing(prev => ({ ...prev, [bet.id]: null }))
    showToast('Challenge declined', 'info')
    setOneVOneInvites(prev => prev.filter(b => b.id !== bet.id))
  }

  const fmt = (dt) => dt ? new Date(dt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'No tee time'
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null
  const totalPending = matchInvites.length + betInvites.length + oneVOneInvites.length + tableInvites.length

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading...</div>
    </div>
  )

  return (
    <div style={s.screen}>
      {toast && <div style={{ ...s.toast, background: toast.type === 'error' ? '#C0392B' : toast.type === 'success' ? '#00563F' : '#1A1814' }}>{toast.msg}</div>}
      <div style={s.body}>
        {totalPending === 0 ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>📬</div>
            <div style={s.emptyText}>No pending invites</div>
            <div style={s.emptySub}>Match, betting and 1v1 invites will appear here</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* 1v1 Challenges */}
            {oneVOneInvites.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={s.sectionTitle}>1V1 CHALLENGES</div>
                {oneVOneInvites.map(bet => {
                  const challenger = oneVOneProfiles[bet.created_by]
                  const a = acting[bet.id]
                  return (
                    <div key={bet.id} style={{ ...s.card, borderColor: 'rgba(192,57,43,0.2)' }}>
                      <div style={s.cardTop}>
                        <div style={s.cardIcon}>⚔️</div>
                        <div style={s.cardInfo}>
                          <div style={s.cardTitle}>{bet.description}</div>
                          <div style={s.cardSub}>From {challenger ? `${challenger.first_name} ${challenger.last_name}` : '—'}</div>
                        </div>
                        <div style={s.betAmt}>${bet.wager_amount}</div>
                      </div>
                      {bet.settle_by && (
                        <div style={s.metaRow}>
                          <div style={s.metaItem}><span style={s.metaLabel}>SETTLE BY </span><span style={s.metaVal}>{fmtDate(bet.settle_by)}</span></div>
                        </div>
                      )}
                      <div style={s.actions}>
                        <button style={{ ...s.acceptBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleAcceptOneVOne(bet)} disabled={!!a}>
                          {a === 'accepting' ? 'Accepting...' : '✓ Accept Challenge'}
                        </button>
                        <button style={{ ...s.declineBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleDeclineOneVOne(bet)} disabled={!!a}>
                          {a === 'declining' ? '...' : 'Decline'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Table Invites */}
            {tableInvites.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={s.sectionTitle}>TABLE INVITES</div>
                {tableInvites.map(invite => {
                  const t = tableDetails[invite.table_id]
                  const creator = tableProfiles[t?.created_by]
                  const a = acting[invite.id]
                  return (
                    <div key={invite.id} style={{ ...s.card, borderColor: 'rgba(201,168,76,0.3)' }}>
                      <div style={s.cardTop}>
                        <div style={s.cardIcon}>🎯</div>
                        <div style={s.cardInfo}>
                          <div style={s.cardTitle}>{t?.name || 'Table'}</div>
                          <div style={s.cardSub}>From {creator ? `${creator.first_name} ${creator.last_name}` : '—'}</div>
                        </div>
                        <div style={s.betAmt}>${t?.wager_amount}</div>
                      </div>
                      <div style={s.metaRow}>
                        <div style={s.metaItem}><span style={s.metaLabel}>FORMAT </span><span style={s.metaVal}>{t?.payout_format === 'winner_takes_all' ? 'Winner Takes All' : 'Custom Split'}</span></div>
                        {t?.lives_enabled && <div style={s.metaItem}><span style={s.metaLabel}>LIVES </span><span style={s.metaVal}>{t?.lives_per_player} per player</span></div>}
                        {t?.scheduled_at && <div style={s.metaItem}><span style={s.metaLabel}>DATE </span><span style={s.metaVal}>{new Date(t.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span></div>}
                      </div>
                      <div style={s.actions}>
                        <button style={{ ...s.acceptBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleAcceptTable(invite)} disabled={!!a}>
                          {a === 'accepting' ? 'Joining...' : '✓ Join Table'}
                        </button>
                        <button style={{ ...s.declineBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleDeclineTable(invite)} disabled={!!a}>
                          {a === 'declining' ? '...' : 'Decline'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Match Invites */}
            {matchInvites.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={s.sectionTitle}>MATCH INVITES</div>
                {matchInvites.map(invite => {
                  const match = invite.match
                  const creator = matchProfiles[match?.creator_id]
                  const a = acting[invite.id]
                  return (
                    <div key={invite.id} style={s.card}>
                      <div style={s.cardTop}>
                        <div style={s.cardIcon}>⛳</div>
                        <div style={s.cardInfo}>
                          <div style={s.cardTitle}>{match?.name || match?.course_name || 'Golf Match'}</div>
                          <div style={s.cardSub}>From {creator ? `${creator.first_name} ${creator.last_name}` : '—'}</div>
                        </div>
                      </div>
                      <div style={s.metaRow}>
                        <div style={s.metaItem}><span style={s.metaLabel}>COURSE </span><span style={s.metaVal}>{match?.course_name || '—'}</span></div>
                        <div style={s.metaItem}><span style={s.metaLabel}>TEE TIME </span><span style={s.metaVal}>{fmt(match?.tee_time)}</span></div>
                      </div>
                      <div style={s.actions}>
                        <button style={{ ...s.acceptBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleAcceptMatch(invite)} disabled={!!a}>
                          {a === 'accepting' ? 'Accepting...' : '✓ Accept'}
                        </button>
                        <button style={{ ...s.declineBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleDeclineMatch(invite)} disabled={!!a}>
                          {a === 'declining' ? 'Declining...' : 'Decline'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Betting Invites */}
            {betInvites.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={s.sectionTitle}>BETTING INVITES</div>
                {betInvites.map(line => {
                  const info = FORMAT_INFO[line.format]
                  const proposer = betProfiles[line.created_by]
                  const match = betMatches[line.match_id]
                  const a = acting[line.id]
                  return (
                    <div key={line.id} style={{ ...s.card, borderColor: 'rgba(201,168,76,0.3)' }}>
                      <div style={s.cardTop}>
                        <div style={s.cardIcon}>{info?.icon || '💰'}</div>
                        <div style={s.cardInfo}>
                          <div style={s.cardTitle}>{info?.label} — ${line.wager_amount}{line.format === 'skins' ? '/skin' : '/player'}</div>
                          <div style={s.cardSub}>From {proposer ? `${proposer.first_name} ${proposer.last_name}` : '—'}</div>
                        </div>
                        <div style={s.betAmt}>${line.wager_amount}</div>
                      </div>
                      <div style={s.metaRow}>
                        <div style={s.metaItem}><span style={s.metaLabel}>MATCH </span><span style={s.metaVal}>{match?.name || match?.course_name || '—'}</span></div>
                        <div style={s.metaItem}><span style={s.metaLabel}>SCORING </span><span style={s.metaVal}>{line.scoring?.toUpperCase()}</span></div>
                      </div>
                      <div style={s.actions}>
                        <button style={{ ...s.acceptBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleAcceptBet(line)} disabled={!!a}>
                          {a === 'accepting' ? 'Accepting...' : '✓ Accept Bet'}
                        </button>
                        <button style={{ ...s.declineBtn, opacity: a ? 0.7 : 1 }} onClick={() => handleDeclineBet(line)} disabled={!!a}>
                          {a === 'declining' ? 'Declining...' : 'Decline'}
                        </button>
                      </div>
                      {match && (
                        <button style={s.viewMatchBtn} onClick={() => onOpenLobby(line.match_id)}>View Match Lobby →</button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  toast: { position: 'fixed', top: 0, left: 0, right: 0, padding: '16px 24px', color: 'white', fontSize: 14, fontWeight: 600, textAlign: 'center', zIndex: 100 },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  empty: { background: '#FFFFFF', borderRadius: 20, padding: 40, border: '1px solid #E0DDD4', textAlign: 'center', marginTop: 24 },
  emptyIcon: { fontSize: 40, marginBottom: 12 },
  emptyText: { fontSize: 16, fontWeight: 700, color: '#1A1814', marginBottom: 6 },
  emptySub: { fontSize: 13, color: '#7A7570' },
  sectionTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 16, color: '#7A7570', letterSpacing: 1.5 },
  card: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 12 },
  cardTop: { display: 'flex', alignItems: 'center', gap: 12 },
  cardIcon: { fontSize: 28, flexShrink: 0 },
  cardInfo: { flex: 1 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#1A1814' },
  cardSub: { fontSize: 12, color: '#7A7570', marginTop: 2 },
  betAmt: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: '#C19A6B' },
  metaRow: { display: 'flex', flexDirection: 'column', gap: 4 },
  metaItem: { fontSize: 13, color: '#1A1814' },
  metaLabel: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.2 },
  metaVal: { fontWeight: 500 },
  actions: { display: 'flex', gap: 10 },
  acceptBtn: { flex: 1, background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  declineBtn: { flex: 1, background: 'transparent', color: '#7A7570', border: '1.5px solid #E0DDD4', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  viewMatchBtn: { background: 'transparent', border: 'none', color: '#00563F', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', padding: 0 },
}
